(ns practitest.frontend.common.permissions
  (:require [re-frame.core :as re-frame]
            [practitest.frontend.subs :as subs]
            [practitest.frontend.common.utils :refer [development-only]]))

;; About permissions model
;;
;; Adopted existing permissions from practitest-app, which is basically a set of strings
;; assigned to user in specific project.
;;
;; Available permissions (from `practitest-app` `app/models/permission.rb`):
;
;PERMISSIONS = [
;    # [name, Title, Description, Other Attributes]
;    ["milestones_view", "Milestones - View", "View Milestones", COLOR_TWO].freeze,
;    ["milestones_comments", "Milestones - Comments", "Add, Edit and Delete comments within Milestones", COLOR_TWO].freeze,
;    ["milestones_editor", "Milestones - Editor", "Create, Edit and View Milestone", COLOR_TWO].freeze,
;    ["milestones_admin", "Milestones - Admin", "Create, Edit, Delete and View Milestones", COLOR_TWO].freeze,
;    ["requirements_view", "Requirements - View", "View Requirements", COLOR_ONE ].freeze,
;    ["requirements_comments", "Requirements - Comments", "Add, Edit and Delete comments within Requirements", COLOR_ONE ].freeze,
;    ["requirements_editor", "Requirements - Editor", "Create, Edit and View Requirements", COLOR_ONE ].freeze,
;    ["requirements_admin", "Requirements - Admin", "Create, Edit, Delete and View Requirements", COLOR_ONE ].freeze,
;
;    # "tests" => { :title => 'Tests', :description => "Create and View Tests, Run Tests" },
;    ["tests_runs_view", "Tests - View", "View Tests, Test Sets, Instances & Runs", COLOR_TWO ].freeze,  #1
;    ["tests_and_sets_comments", "Tests - Comments", "[Tests - View] + Create and Delete my comments in Tests, Test Sets, Instances, and Runs", COLOR_TWO ].freeze, #2
;    ["runs", "Runs - Editor", "[Tests - Comments] + Run Tests, Create and Edit Runs", COLOR_TWO ].freeze, #3
;    ["runs_admin", "Runs - Admin", "[Runs - Editor\" + Delete Runs", COLOR_TWO ].freeze,  #4

;    ["instances_editor", "Instances - Editor", "[Runs - Editor] + Create and Edit Instances", COLOR_TWO ].freeze,
;    ["instances_admin", "Instances - Admin", "[Instances - Editor] + Delete Instances", COLOR_TWO ].freeze,
;    ["sets_editor", "Test Sets - Editor", "[Instances - Editor] + Create and Edit Test Sets", COLOR_TWO ].freeze,
;    ["sets_admin", "Test Sets - Admin", "[Test Sets - Editor] + Delete Test Sets", COLOR_TWO ].freeze,

;    ["tests_editor", "Tests - Editor", "[Tests - Comments] + Create and Edit Tests", COLOR_TWO ].freeze, #7
;    ["tests_admin", "Tests - Admin", "[Tests - Editor] + [Test Sets and Instances - Admin] + Delete Tests", COLOR_TWO ].freeze, #8
;
;    ["issues_view", "Issues - View", "View Issues", COLOR_ONE ].freeze,
;    ["issues_comments", "Issues - Comments", "Add, Edit and Delete comments within Issues", COLOR_ONE ].freeze,
;    ["issues_status", "Issues - Status Modifier", "View Issues; Change Issue Status (by workflow transitions)", COLOR_ONE ].freeze,
;    ["issues_editor", "Issues - Editor", "Create, Edit and View Issues", COLOR_ONE ].freeze,
;    ["issues_admin", "Issues - Admin", "Create, Edit, Delete and View Issues. Delete on specific projects", COLOR_ONE ].freeze,
;
;    ["reports", "Reports", "View, Create, Edit and Delete Reports", COLOR_TWO ].freeze,
;    ["dashboard_edit", "Dashboard Customization", "Customize dashboard items", COLOR_TWO ].freeze,
;    ["dashboard_private", "Dashboard - Private", "Customize a private dashboard", COLOR_TWO ].freeze,
;
;    ["customization", "Customization", "Customize Custom fields", COLOR_TWO ].freeze,
;    ["custom_views_all", "All Filters", "Edit / Delete Any existing filter", COLOR_TWO ].freeze,
;    ["notification_list", "Notification List", "Enable users to add OTHER users to a notification list of an entity", COLOR_TWO ].freeze,
;    ["workflow", "Workflow", "Customize Issue status workflow", COLOR_TWO ].freeze,
;    ["users_n_per", "Project, Users & Permissions, Integrations", "Add and Delete Users & Groups, Customize Permissions, Change Project Name and Logo, Set Integrations and Webhooks", COLOR_TWO ].freeze,
;    ["import_export", "Import and Export", "Import data to modules and Export data from modules", COLOR_TWO ].freeze,
;  ].freeze
;

(def ^:private all-permissions
  #{"milestones_view" "milestones_comments" "milestones_editor" "milestones_admin"
    "requirements_view" "requirements_comments" "requirements_editor" "requirements_admin"
    "tests_runs_view" "tests_and_sets_comments" "runs" "runs_admin" "instances_editor" "instances_admin" "sets_editor" "sets_admin" "tests_editor" "tests_admin"
    "issues_view" "issues_comments" "issues_status" "issues_editor" "issues_admin"
    "reports" "dashboard_edit" "dashboard_private" "customization"
    "custom_views_all" "notification_list" "workflow" "users_n_per"
    "import_export"})

(re-frame/reg-sub
  ::has-permission?
  :<- [::subs/current-project]
  ;; TODO: move user-projects inside current-user otherwise there can be a mismatch in permissions check
  :<- [::subs/current-user]
  :<- [::subs/user-projects]
  (fn [[current-project current-user user-projects] [_ permission]]
    (when (nil? permission)
      (js/console.warn "nil permission sub call - should not happen"))

    (when-not (contains? all-permissions permission)
      (js/console.warn "Invalid permission" permission "was specified for has-permission?"))

    (let [user-project (first (filter #(= (:id (:project %))
                                          (:id current-project))
                                      user-projects))]
      (contains? (:permissions user-project) permission))))

(re-frame/reg-sub
  ::project-administrator?
  :<- [::subs/current-user]
  ;; TODO: move user-projects inside current-user otherwise there can be a mismatch in permissions check
  :<- [::subs/user-projects]
  (fn [[current-user user-projects] [_ project]]
    (let [user-project (first (filter #(= (:id (:project %))
                                          (:id project))
                                      user-projects))]
      (:is_admin user-project))))

;; TODO: check permissions in router/controllers as well
(defn has-permission?
  "Checks that current user has specified permission in current project.

  Uses re-frame subscription under the hood, so will be reactive in reagent components"
  [permission]
  @(re-frame/subscribe [::has-permission? permission]))

(defn project-administrator?
  "Checks that current user has administrator permissions for specified project.

  Uses re-frame subscription under the hood, so will be reactive in reagent components"
  [project]
  @(re-frame/subscribe [::project-administrator? project]))

;; TODO: move to common entities metadata ?
(def ^:private comment-permission-per-entity
  {:issue "issues_comments"
   :requirement "requirements_comments"
   :milestone "milestones_comments"
   :test "tests_and_sets_comments"
   :test-step "tests_and_sets_comments"
   :testset "tests_and_sets_comments"})

(defn comment-for-entity
  [entity-type]
  (get comment-permission-per-entity entity-type))

;; Section to quickly visually test different permission groups
(development-only
  (def administrator-permissions all-permissions)

  ;; Default tester permissions
  (def tester-permissions #{"milestones_view" "milestones_comments" "milestones_editor"
                            "requirements_view" "requirements_comments" "requirements_editor" "requirements_admin"
                            "tests_runs_view" "tests_and_sets_comments" "runs" "runs_admin" "instances_editor" "instances_admin" "sets_editor" "sets_admin" "tests_editor" "tests_admin"
                            "issues_view" "issues_comments" "issues_status" "issues_editor"
                            "reports"
                            "import_export"})

  ;; Default developers permissions
  (def developer-permissions #{"milestones_view"
                               "requirements_view"
                               "tests_runs_view"
                               "issues_view" "issues_comments" "issues_status" "issues_editor"
                               "reports"})

  (def readonly-group #{"milestones_view"
                        "requirements_view"
                        "tests_runs_view"
                        "issues_view"})

  (def issues-only-group #{"issues_view"})

  (re-frame/reg-event-db
    ::replace-user-permissions!
    (fn [db [_ permissions]]
      (let [current-project (:current-project db)]
        (update db :user-projects
                (fn [user-projects]
                  (reduce
                    (fn [acc v]
                      (if (= (:id (:project v)) (:id current-project))
                        ;; Override permissions for current project
                        (conj acc (assoc v :permissions permissions))
                        (conj acc v)))
                    []
                    user-projects))))))

  ;; Uncomment to quickly test different permission sets for current project
  ;(re-frame/dispatch [::replace-user-permissions! readonly-group])
  ;(re-frame/dispatch [::replace-user-permissions! administrator-permissions])
  ;; (re-frame/dispatch [::replace-user-permissions! #{"milestones_view" "milestones_comments" "milestones_editor"
  ;;                           "tests_runs_view" "tests_and_sets_comments" "runs" "runs_admin" "instances_admin" "instance_editor"
  ;;                           "tests_editor" "tests_admin" "import_export"}])

  ;; Or you can use REPL to dispatch those commands as well
  ;; TODO: buttons in sandbox or something like that ?
  (comment
    (re-frame/dispatch [::replace-user-permissions! administrator-permissions])
    (re-frame/dispatch [::replace-user-permissions! tester-permissions])
    (re-frame/dispatch [::replace-user-permissions! readonly-group])
    (re-frame/dispatch [::replace-user-permissions! issues-only-group])
    ))
